import {
  Box,
  Button,
  Grid,
  Switch,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  DataGrid,
  gridClasses,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const PRIMARY = "#0B5C8A";

const memberPillStyle = {
  display: "inline-flex",
  alignItems: "center",
  maxWidth: "100%",
  padding: "2px 9px",
  borderRadius: 999,
  background: "#EAF2FB",
  border: "1px solid #C9DEF3",
  color: "#1B4F82",
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.5,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const selfPillStyle = {
  ...memberPillStyle,
  background: "#F1F4F8",
  border: `1px solid ${LINE}`,
  color: MUTED,
};

const Teams = () => {
  const [teams, setTeams] = useState([]);
  const orgId = JSON.parse(localStorage.getItem("org"));
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 750px)");

  useEffect(() => {
    const getOrg = async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/organization/get-by-admin?username=${storedUser.username}&orgId=${orgId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      if (
        !res.data.orgLogo &&
        !res.data.orgUsername &&
        !res.data.orgShortform
      ) {
        navigate(`/dashboard/org-details`);
      }
    };
    getOrg();
  }, []);

  useEffect(() => {
    const getTeams = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/team/all?orgId=${orgId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setTeams(res.data);
      } catch (error) {
        console.log(error.response.data.Exception || "Access denied");
      }
    };
    getTeams();
  }, []);

  const columns = [
    {
      field: "teamId",
      headerName: "Team ID",
      flex: 1,
      renderCell: (params) => (
        <span style={{ fontFamily: MONO, fontSize: 12.5, color: INK_2 }}>{params.value}</span>
      ),
    },
    {
      field: "name",
      headerName: "Team Name",
      flex: 1,
      renderCell: (params) => (
        <span style={{ fontWeight: 700, color: INK }}>{params.value}</span>
      ),
    },
    {
      field: "orgUsers",
      headerName: "Members",
      flex: 1,
      renderCell: (params) => (
        <span
          style={{
            color: params.row?.orgUsers?.length > 0 ? INK_2 : MUTED,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={
            params.row?.orgUsers?.length > 0
              ? params.row?.orgUsers?.map((user) => user.name).join(", ")
              : "Self"
          }
        >
          {params.row?.orgUsers?.length > 0
            ? params.row?.orgUsers?.map((user) => user.name).join(", ")
            : "Self"}
        </span>
      ),
    },
    {
      field: "projects",
      headerName: "Projects",
      flex: 1,
      renderCell: (params) => (
        <span
          style={{ color: INK_2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          title={params.row?.projects?.map((p) => p.projectName).join(", ")}
        >
          {params.row?.projects?.map((p) => p.projectName).join(", ")}
        </span>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <>
          <Link to={`/dashboard/edit-team/${params.row.teamId}`} style={{ textDecoration: "none" }}>
            <Button
              sx={{
                minWidth: 0,
                height: 28,
                px: 1.25,
                borderRadius: "8px",
                border: "1px solid #D5DEE7",
                background: "#FFFFFF",
                color: INK,
                fontFamily: FONT,
                fontSize: 12.5,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { background: "#F4F7FA" },
              }}
            >
              Edit
            </Button>
          </Link>
        </>
      ),
    },
  ];

  const rows = teams ? teams : "";

  const handleEnableDisable = async (orgUserId, isEnable) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/org-user?orgUserId=${orgUserId}`,
        {
          isEnable: !isEnable,
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
    } catch (error) {
      console.log(error);
    }
  };
  return (
    <div style={{ fontFamily: FONT, color: INK }}>
      <Box
        sx={{
          display: { xs: "block", sm: "flex" },
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography
          variant="h4"
          my={1}
          fontWeight={700}
          sx={{ fontFamily: FONT, fontSize: { xs: 24, sm: 27 }, fontWeight: 800, letterSpacing: "-0.028em", color: INK, mb: { xs: 1.5, sm: 0 } }}
        >
          List of Teams
        </Typography>
        <Link to={`/dashboard/create-team`} style={{ textDecoration: "none" }}>
          <Button
            className="btn-contained"
            sx={{
              width: { xs: "100%", sm: "auto" },
              mb: 0,
              height: { xs: 44, sm: 36 },
              px: 2,
              gap: 1,
              borderRadius: "9px !important",
              background: `${PRIMARY} !important`,
              color: "#FFFFFF !important",
              border: "0 !important",
              fontFamily: FONT,
              fontSize: 13.5,
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "none",
              whiteSpace: "nowrap",
              "&:hover": { background: "#084767 !important" },
            }}
          >
            <PersonAddAltRoundedIcon sx={{ fontSize: 18 }} /> Create New Team
          </Button>
        </Link>
      </Box>
      <div className="dashboard-container" style={{ padding: 0, background: "transparent", boxShadow: "none" }}>
        {isMobile ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {teams?.map((team) => (
              <div
                key={team.teamId}
                className="card"
                style={{
                  background: "#FFFFFF",
                  border: `1px solid ${LINE}`,
                  borderRadius: 14,
                  padding: "14px 14px 8px",
                  boxShadow: "none",
                  margin: 0,
                }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div style={{ minWidth: 0 }}>
                    <Typography
                      fontSize={"16px"}
                      color={INK}
                      fontWeight={"800"}
                      sx={{ fontFamily: FONT, letterSpacing: "-0.015em" }}
                    >
                      {team.name}
                    </Typography>
                    <Typography
                      fontSize={"12px"}
                      color={MUTED}
                      fontWeight={"500"}
                      sx={{ fontFamily: MONO, mt: 0.25 }}
                    >
                      ID: {team.teamId}
                    </Typography>
                  </div>
                </div>
                <Typography
                  color={MUTED}
                  fontSize={"10.5px"}
                  fontWeight={700}
                  mt={1.75}
                  mb={0.75}
                  sx={{ display: "flex", alignItems: "center", gap: 2, fontFamily: FONT, letterSpacing: "0.11em" }}
                >
                  MEMBERS
                </Typography>
                <Grid container spacing={0.75} mb={1.5}>
                  {team?.orgUsers?.length > 0 ? (
                    team?.orgUsers?.map((user) => (
                      <Grid size={{ xs: 6 }}>
                        <div
                          className="card-tag"
                          style={{ ...memberPillStyle, display: "block", textAlign: "center" }}
                        >
                          {user.name}
                        </div>
                      </Grid>
                    ))
                  ) : (
                    <Grid size={{ xs: 6 }}>
                      <div className="card-tag" style={{ ...selfPillStyle, display: "block", textAlign: "center" }}>
                        Self
                      </div>
                    </Grid>
                  )}
                </Grid>
                <Typography
                  color={INK_2}
                  fontSize={"13px"}
                  fontWeight={500}
                  mb={1.25}
                  sx={{ display: "flex", alignItems: "start", gap: 1, fontFamily: FONT, lineHeight: 1.45 }}
                >
                  <MeetingRoomOutlinedIcon sx={{ fontSize: 16, color: MUTED, mt: "1px" }} />{" "}
                  {team?.projects?.map((p) => p.projectName).join(", ")}
                </Typography>
                <hr style={{ border: 0, borderTop: "1px solid #F1F4F8", margin: "0 -14px 6px" }} />
                <div style={{ display: "flex", justifyContent: "end" }}>
                  <Button
                    onClick={() => navigate(`/dashboard/edit-team/${team.teamId}`)}
                    sx={{
                      minWidth: 0,
                      width: 44,
                      height: 44,
                      borderRadius: "12px",
                      border: `1px solid ${LINE}`,
                      background: "#F4F7FA",
                      color: `${INK_2} !important`,
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </Button>
                </div>
              </div>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              background: "#fff",
              border: `1px solid ${LINE}`,
              borderRadius: "14px",
              overflow: "hidden",
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 20,
                  },
                },
              }}
              pageSizeOptions={[20]}
              disableRowSelectionOnClick
              disableColumnSelector
              disableDensitySelector
              slots={{ toolbar: CustomToolbar }}
              getRowId={(row) => row.teamId}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
              }
              rowHeight={50}
              columnHeaderHeight={40}
              sx={{
                position: "relative",
                border: "none",
                fontFamily: `${FONT} !important`,
                color: INK,

                "& .MuiDataGrid-columnHeaderTitle": {
                  fontFamily: FONT,
                  fontWeight: "700 !important",
                  fontSize: 10.5,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                  color: MUTED,
                },
                "& .MuiDataGrid-columnHeaders": {
                  background: "#F7F9FC !important",
                  color: MUTED,
                  fontSize: "14px",
                  borderRadius: 0,
                  borderTop: `1px solid ${LINE}`,
                  borderBottom: `1px solid ${LINE}`,
                },
                "& .MuiDataGrid-columnHeader, & .MuiDataGrid-filler": {
                  background: "#F7F9FC !important",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#F4F7FA",
                },
                "& .MuiDataGrid-cell": {
                  fontSize: "13.5px",
                  border: "none",
                  borderBottom: "1px solid #F1F4F8",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnSeparator": {
                  display: "none",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: `1px solid ${LINE}`,
                },
                "& .MuiTablePagination-root, & .MuiTablePagination-displayedRows": {
                  fontFamily: MONO,
                  fontSize: 12,
                  color: MUTED,
                },
                "& .css-1ccsd5i-MuiButtonBase-root-MuiButton-root": {
                  color: "#000 !important",
                  border: "1px solid #B0B0B0",
                  borderRadius: "12px",
                  padding: 1,
                },
                "& .MuiInputBase-root-MuiInput-root::after": {
                  borderBottom: "none !important",
                },
                "& .MuiInputBase-root-MuiInput-root": {
                  borderBottom: "none !important",
                },
                [`& .${gridClasses.row}.even`]: {
                  backgroundColor: "#FFFFFF",
                  // '&:hover': {
                  //   backgroundColor: "#f6f6f6",
                  // },
                },
                [`& .${gridClasses.row}.odd`]: {
                  backgroundColor: "#FCFDFE",
                },
                [`& .${gridClasses.row}.even:hover, & .${gridClasses.row}.odd:hover`]: {
                  backgroundColor: "#F4F7FA",
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none !important",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none !important",
                },
                "& .css-5h82ro-MuiInputBase-root-MuiInput-root::after": {
                  borderBottom: "none !important",
                },
              }}
            />
          </Box>
        )}
      </div>
    </div>
  );
};

export default Teams;

function CustomToolbar() {
  return (
    <GridToolbarContainer
      sx={{ display: "flex", justifyContent: "end", px: 1.75, py: 1.25, gap: 1 }}
    >
      <GridToolbarQuickFilter
        sx={{
          border: "1px solid #D5DEE7",
          borderRadius: "9px",
          padding: "3px 10px",
          background: "#FFFFFF",
          "&:hover": {
            backgroundColor: "#F4F7FA",
          },
          "&:focus-within": {
            borderColor: PRIMARY,
          },
          "& .MuiInputBase-root": {
            borderRadius: "9px",
            fontFamily: FONT,
            fontSize: 13.5,
          },
          "& .MuiInputBase-root::before, & .MuiInputBase-root::after": {
            display: "none",
          },
          "& .MuiSvgIcon-root": { color: MUTED, fontSize: 18 },
          width: 300,
        }}
      />
      {/* <GridToolbarFilterButton /> */}
    </GridToolbarContainer>
  );
}
